'use client';

import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Sparkles } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useRef } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { PlatformSavings } from '@/features/landing/actions/getPlatformStatsAction';
import { AppRatingLine } from '@/features/landing/components/app-rating-line';
import { BookingLink } from '@/features/landing/components/booking-link';
import { LoopVideo } from '@/features/landing/components/loop-video';
import { HeroSavingsStats } from '@/features/landing/components/sections/hero-savings-stats';
import { useBookingLink } from '@/features/landing/hooks/useBookingLink';
import { useLandingCTATracking } from '@/features/landing/hooks/useLandingCTATracking';
import { useSectionVisibility } from '@/features/landing/hooks/useSectionVisibility';
import { Link } from '@/i18n/routing';

// Staggers the children's entrance. No opacity of its own, so the server HTML of the
// column (and the subheadline in it) is visible before hydration.
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    },
  },
} as const;

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring' as const,
      stiffness: 100,
      damping: 15,
    },
  },
};

interface HeroSectionProps {
  /** Null hides the savings block under the video. */
  stats: PlatformSavings | null;
}

export function HeroSection({ stats }: HeroSectionProps) {
  const t = useTranslations('landing.hero');
  const bookingLink = useBookingLink();
  const containerRef = useRef<HTMLElement>(null);
  const { trackCTAClick } = useLandingCTATracking();
  const { ref: visibilityRef } = useSectionVisibility('hero');
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start'],
  });

  const y = useTransform(scrollYProgress, [0, 1], [0, 50]);

  const handlePrimaryCTAClick = () => {
    trackCTAClick({
      ctaLocation: 'hero_primary',
      ctaText: t('cta.pilot'),
      ctaType: 'primary',
      section: 'hero',
      destinationUrl: bookingLink.href,
    });
  };

  return (
    <section
      id="home"
      ref={(el) => {
        // Combine refs for both scroll tracking and section visibility
        (containerRef as React.MutableRefObject<HTMLElement | null>).current = el;
        (visibilityRef as React.MutableRefObject<HTMLElement | null>).current = el;
      }}
      className="relative w-full py-12 lg:py-16 overflow-hidden bg-background border-b border-border"
    >
      <div className="container relative z-10 mx-auto px-4 md:px-6 lg:px-8 max-w-[1440px]">
        <div className="grid items-center gap-8 lg:grid-cols-2 lg:gap-12 xl:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] w-full">
          {/* Left Column: Text Content */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-5 sm:space-y-6 text-center lg:text-left"
          >
            {/* Animated Badge */}
            <motion.div variants={itemVariants} className="flex justify-center lg:justify-start">
              <motion.div
                whileHover={{ scale: 1.05 }}
                transition={{ type: 'spring', stiffness: 400, damping: 17 }}
              >
                <Badge
                  variant="outline"
                  className="px-4 py-1.5 text-sm border-border bg-card/50 text-foreground backdrop-blur-sm shadow-sm cursor-default"
                >
                  <motion.span
                    animate={{ rotate: [0, 15, -15, 0] }}
                    transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                    className="inline-block"
                  >
                    <Sparkles className="w-3.5 h-3.5 mr-2 text-primary" />
                  </motion.span>
                  {t('badge')}
                </Badge>
              </motion.div>
            </motion.div>

            {/* Heading */}
            <motion.h1
              variants={itemVariants}
              className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground md:text-5xl lg:text-6xl xl:text-5xl leading-tight text-balance px-2 sm:px-0"
            >
              {t.rich('title', {
                highlight: (chunks) => <span className="text-primary inline-block">{chunks}</span>,
                danger: (chunks) => <span className="text-red-500/70 inline-block">{chunks}</span>,
              })}
            </motion.h1>

            {/* Description: static, painted with the first HTML (the mobile LCP element) */}
            <p
              data-testid="landing-hero-subheadline"
              className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto lg:mx-0 text-balance px-2 sm:px-0"
            >
              {t.rich('description', {
                highlight: (chunks) => <span className="text-primary font-medium">{chunks}</span>,
                danger: (chunks) => <span className="text-red-400/60 font-normal">{chunks}</span>,
              })}
            </p>

            {/* CTA Button */}
            <motion.div
              variants={itemVariants}
              className="flex items-center justify-center lg:justify-start pt-2 sm:pt-4"
            >
              <motion.div
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                transition={{ type: 'spring', stiffness: 400, damping: 17 }}
                className="relative group"
              >
                {/* Subtle glow effect behind button */}
                <div className="absolute -inset-0.5 bg-gradient-to-r from-primary to-emerald-400 rounded-xl blur-md opacity-40 group-hover:opacity-70 transition-opacity duration-500" />

                <Button
                  asChild
                  size="lg"
                  className="relative h-12 sm:h-14 px-6 sm:px-10 text-base sm:text-lg font-semibold cursor-pointer w-full sm:w-auto max-w-xs sm:max-w-none bg-gradient-to-r from-primary via-emerald-500 to-primary bg-[length:200%_100%] hover:bg-[length:100%_100%] transition-[background-size] duration-500 text-white border-0 shadow-2xl shadow-primary/40 overflow-hidden"
                >
                  <BookingLink
                    location="hero_primary"
                    onClick={handlePrimaryCTAClick}
                    className="relative z-10 flex items-center gap-2"
                  >
                    {/* Shimmer effect - hidden on hover */}
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 group-hover:opacity-0 transition-opacity duration-200"
                      animate={{
                        x: ['-200%', '200%'],
                      }}
                      transition={{
                        duration: 0.7,
                        repeat: Infinity,
                        repeatDelay: 2.5,
                        ease: 'easeInOut',
                      }}
                    />
                    <span className="relative z-10">{t('cta.pilot')}</span>
                    {/* Animated arrow - hidden on hover */}
                    <motion.span
                      animate={{ x: [0, 6, -1, 0] }}
                      transition={{
                        duration: 0.5,
                        repeat: Infinity,
                        repeatDelay: 2.5,
                        times: [0, 0.4, 0.7, 1],
                        ease: [0.25, 0.46, 0.45, 0.94],
                      }}
                      className="inline-block relative z-10 group-hover:opacity-0 transition-opacity duration-150"
                    >
                      <ArrowRight className="h-5 w-5" />
                    </motion.span>
                    {/* Static arrow - visible on hover, more prominent */}
                    <span className="absolute right-6 sm:right-10 z-10 opacity-0 group-hover:opacity-100 translate-x-0 group-hover:translate-x-2 scale-100 group-hover:scale-125 transition-all duration-200">
                      <ArrowRight className="h-5 w-5" />
                    </span>
                  </BookingLink>
                </Button>
              </motion.div>
            </motion.div>

            {/* Social Proof / Crystal Balls */}
            <motion.div
              variants={itemVariants}
              className="pt-4 sm:pt-5 border-t border-border/50 mt-4 sm:mt-5"
            >
              <div className="flex items-center gap-3 sm:gap-6 flex-wrap justify-center lg:justify-start">
                <div className="flex items-center -space-x-2 sm:-space-x-3">
                  {/* Active Slot - Reduced visual prominence */}
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.6 }}
                    className="relative z-10 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-red-500/10 to-orange-900/10 border border-red-500/20 flex items-center justify-center backdrop-blur-sm"
                  >
                    <span className="text-sm sm:text-base filter drop-shadow-sm">🔮</span>
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.9, type: 'spring', stiffness: 500 }}
                      className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-red-500/80 rounded-full border border-background flex items-center justify-center"
                    >
                      <svg
                        className="w-1.5 h-1.5 sm:w-2 sm:h-2 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </motion.div>
                  </motion.div>

                  {/* Empty Slots - Easter Egg Links to Pilot Section */}
                  {[2, 3, 4, 5].map((i, index) => (
                    <motion.div
                      key={i}
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{
                        type: 'spring',
                        stiffness: 200,
                        damping: 15,
                        delay: 0.7 + index * 0.1,
                      }}
                      whileHover={{ scale: 1.15, zIndex: 20 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Link
                        href="/#pilot"
                        onClick={(e) => {
                          e.preventDefault();
                          const pilotSection = document.querySelector('#pilot');
                          if (pilotSection) {
                            pilotSection.scrollIntoView({
                              behavior: 'smooth',
                              block: 'start',
                            });
                          }
                        }}
                        className="group w-8 h-8 sm:w-10 sm:h-10 rounded-full border border-dashed border-border bg-card/30 flex items-center justify-center transition-colors hover:border-primary hover:bg-primary/10 cursor-pointer"
                      >
                        <span className="text-sm sm:text-base opacity-20 grayscale group-hover:opacity-60 group-hover:grayscale-0 transition-all">
                          🔮
                        </span>
                      </Link>
                    </motion.div>
                  ))}
                </div>

                <div className="flex flex-col text-left">
                  <span className="text-sm font-medium text-foreground">
                    {t('socialProof.fired')}
                  </span>
                  <span className="text-xs text-muted-foreground">{t('socialProof.ready')}</span>
                </div>
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="pt-3">
              <AppRatingLine />
            </motion.div>
          </motion.div>

          <div className="flex items-center justify-end h-full flex-col gap-4">
            {/* Right Column: Video */}
            <motion.div
              style={{ y }}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative mx-auto w-full max-w-[720px] lg:max-w-none"
            >
              {/* Same aspect as the hero loop (1548x1080), so the poster and video never shift layout */}
              <div className="relative aspect-[1548/1080] overflow-hidden rounded-2xl border border-border bg-muted shadow-2xl">
                <LoopVideo name="hero-loop" testId="landing-hero-video" />
              </div>
            </motion.div>
            {stats && <HeroSavingsStats stats={stats} />}
          </div>
        </div>
      </div>
    </section>
  );
}
